/**
 * Hero Block - Frontend JavaScript
 *
 * Handles background video pause/play toggle and prefers-reduced-motion.
 * Supports self-hosted <video>, YouTube iframes, and Vimeo iframes.
 */
( function () {
	'use strict';

	const hero = document.querySelector( '.commcore-hero--home' );
	if ( ! hero ) {
		return;
	}

	const videoEl = hero.querySelector( '.commcore-hero__video' );
	const toggle = hero.querySelector( '.commcore-hero__video-toggle' );

	if ( ! videoEl || ! toggle ) {
		return;
	}

	const LABEL_PAUSE = toggle.getAttribute( 'aria-label' ) || 'Pause background video';
	const LABEL_PLAY = LABEL_PAUSE.replace( 'Pause', 'Play' );

	// Detect video source type.
	const isIframe = videoEl.tagName === 'IFRAME';
	const videoSource = hero.getAttribute( 'data-video-source' ) || 'file';

	let isPlaying = false;

	/**
	 * Set the toggle to the "playing" or "paused" visual state.
	 *
	 * @param {boolean} playing
	 */
	function setToggleState( playing ) {
		isPlaying = playing;
		toggle.setAttribute( 'data-playing', playing ? 'true' : 'false' );
		toggle.setAttribute( 'aria-label', playing ? LABEL_PAUSE : LABEL_PLAY );
	}

	/**
	 * Send a postMessage command to the iframe.
	 *
	 * @param {string} action 'pause' or 'play'
	 */
	function sendIframeCommand( action ) {
		if ( ! videoEl.contentWindow ) {
			return;
		}

		if ( videoSource === 'youtube' ) {
			const func = action === 'pause' ? 'pauseVideo' : 'playVideo';
			videoEl.contentWindow.postMessage(
				JSON.stringify( { event: 'command', func: func, args: '' } ),
				'*'
			);
		} else if ( videoSource === 'vimeo' ) {
			const method = action === 'pause' ? 'pause' : 'play';
			videoEl.contentWindow.postMessage(
				JSON.stringify( { method: method } ),
				'*'
			);
		}
	}

	/**
	 * Activate the iframe by copying data-src → src.
	 */
	function activateIframe() {
		const dataSrc = videoEl.getAttribute( 'data-src' );
		if ( dataSrc && ! videoEl.getAttribute( 'src' ) ) {
			videoEl.setAttribute( 'src', dataSrc );
		}
	}

	/**
	 * Deactivate the iframe by removing src.
	 */
	function deactivateIframe() {
		if ( videoEl.getAttribute( 'src' ) ) {
			videoEl.setAttribute( 'src', '' );
			videoEl.removeAttribute( 'src' );
		}
	}

	/**
	 * Pause/play toggle click handler.
	 */
	toggle.addEventListener( 'click', function () {
		if ( isIframe ) {
			if ( isPlaying ) {
				sendIframeCommand( 'pause' );
				setToggleState( false );
			} else {
				activateIframe();
				sendIframeCommand( 'play' );
				setToggleState( true );
			}
		} else {
			if ( videoEl.paused ) {
				videoEl.play();
				setToggleState( true );
			} else {
				videoEl.pause();
				setToggleState( false );
			}
		}
	} );

	/**
	 * Listen for postMessage events from YouTube / Vimeo to detect video end.
	 */
	if ( isIframe ) {
		window.addEventListener( 'message', function ( event ) {
			let data;
			try {
				data = typeof event.data === 'string' ? JSON.parse( event.data ) : event.data;
			} catch ( e ) {
				return;
			}

			// YouTube: state 0 = ended, state 1 = playing.
			if ( videoSource === 'youtube' && data.event === 'onStateChange' ) {
				if ( data.info === 0 ) {
					setToggleState( false );
				} else if ( data.info === 1 ) {
					setToggleState( true );
				}
			}

			// Vimeo: event-based messages.
			if ( videoSource === 'vimeo' && data.event ) {
				if ( data.event === 'finish' || data.event === 'ended' ) {
					setToggleState( false );
				} else if ( data.event === 'play' ) {
					setToggleState( true );
				}
			}
		} );
	}

	/**
	 * When the native video ends (no loop), update the toggle to "play" state.
	 */
	if ( ! isIframe ) {
		videoEl.addEventListener( 'ended', function () {
			setToggleState( false );
		} );
	}

	/**
	 * Respect prefers-reduced-motion.
	 *
	 * If the user prefers reduced motion, pause the video immediately.
	 * For iframes, prevent loading by not setting src.
	 * Also listen for changes so the user can toggle the system setting.
	 */
	const motionQuery = window.matchMedia( '(prefers-reduced-motion: reduce)' );

	function handleMotionPreference( mq ) {
		if ( mq.matches ) {
			if ( isIframe ) {
				deactivateIframe();
			} else {
				videoEl.pause();
			}
			setToggleState( false );
		} else {
			// Motion is OK — activate iframe if not already active.
			if ( isIframe ) {
				activateIframe();
				setToggleState( true );
			}
		}
	}

	// On initial load: check motion preference, then activate if OK.
	if ( isIframe ) {
		if ( ! motionQuery.matches ) {
			activateIframe();
			setToggleState( true );
		} else {
			setToggleState( false );
		}
	} else {
		handleMotionPreference( motionQuery );
		// Native video autoplays — set initial state.
		if ( ! motionQuery.matches ) {
			setToggleState( true );
		}
	}

	motionQuery.addEventListener( 'change', handleMotionPreference );
} )();
